import React, { useEffect, useMemo, useState } from "react";
import { Tabs, notification } from "antd";
import Spinner from "../../../commons/Spinner/Spinner";
import { DropdownWithSearch } from "../../../commons/Dropdown/DropdownWithSearch";
import useNotificationTypes from "../../../state/hooks/notifications-management/useNotificationTypes";
import useNotificationKeysValues from "../../../state/hooks/notifications-management/useNotificationKeysValues";
import { INotificationKeysValues } from "../../../models/Interface/NotificationManagement.interface";
import { DEFAULT_PREVIEW_LANGUAGE, LANGUAGE_OPTIONS } from "../constants";
import {
  buildPreviewRows,
  countMissingValues,
  getApiErrorMessage,
  groupKeysValuesByType,
  IPreviewRow,
  isMissingValue,
} from "../helpers";
import {
  CountBadge,
  MONO_FONT,
  MonoText,
  PreviewImage,
} from "./NotificationCells";
import PushPreview from "./PushPreview";

const ALL_TYPES = "__all_types__";

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span
    className="text-xs font-bold"
    style={{
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    }}
  >
    {children}
  </span>
);

/** One localization key and the value it resolves to in the chosen language. */
const KeyRow = ({ row, language }: { row: IPreviewRow; language: string }) => {
  const missing = isMissingValue(row);
  const notConfigured = !row.keyName;

  return (
    <div
      className="flex flex-col gap-1"
      style={
        missing
          ? {
              background: "#FEF3F2",
              border: "1px solid #FDA29B",
              borderRadius: 8,
              padding: "10px 12px",
              margin: "6px 0",
            }
          : {
              padding: "12px 0",
              borderBottom: "1px solid #F0F1F5",
            }
      }
    >
      <div className="flex items-center justify-between gap-10 flex-wrap">
        <div className="flex items-baseline gap-10 min-w-0 flex-wrap">
          <FieldLabel>{row.label}</FieldLabel>
          {row.keyName ? (
            <MonoText value={row.keyName} />
          ) : (
            <span className="text-xs" style={{ color: "#C4C7D0" }}>
              no key on this type
            </span>
          )}
        </div>
        {missing && <CountBadge tone="error">Missing in {language}</CountBadge>}
      </div>

      {notConfigured ? (
        <span className="text-sm" style={{ color: "#C4C7D0" }}>
          —
        </span>
      ) : missing ? (
        <span className="text-sm font-semibold" style={{ color: "#B42318" }}>
          Key not found in the {language} localization file
        </span>
      ) : (
        <div className="flex items-start gap-10 min-w-0">
          {row.isUrl && <PreviewImage src={row.value} size={40} />}
          {/* Raw localization string — placeholders stay literal. */}
          <span
            className="text-sm text-dark-grey"
            style={{
              wordBreak: "break-word",
              lineHeight: 1.5,
              fontFamily: row.isUrl ? MONO_FONT : undefined,
              fontSize: row.isUrl ? 12 : undefined,
              opacity: row.isUrl ? 0.7 : 1,
            }}
          >
            {row.value}
          </span>
        </div>
      )}
    </div>
  );
};

const KeySetBody = ({
  entry,
  language,
}: {
  entry: INotificationKeysValues;
  language: string;
}) => (
  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-30 items-start">
    <div className="flex flex-col min-w-0">
      {buildPreviewRows(entry).map((row) => (
        <KeyRow key={row.label} row={row} language={language} />
      ))}
      <div className="flex flex-col gap-1" style={{ padding: "12px 0" }}>
        <FieldLabel>Web link</FieldLabel>
        <span
          className="text-sm text-dark-grey"
          style={{ wordBreak: "break-all", opacity: 0.7, fontSize: 12 }}
        >
          {entry.webNotificationLink || "—"}
        </span>
      </div>
    </div>

    <div className="flex flex-col gap-10 min-w-0">
      <PushPreview entry={entry} showMeta={false} />
      <span className="text-xs" style={{ color: "#9CA3AF" }}>
        How this key set arrives on a {language} device.
      </span>
    </div>
  </div>
);

const NotificationPreviewPanel = () => {
  const [language, setLanguage] = useState<string>(DEFAULT_PREVIEW_LANGUAGE);
  const [selectedType, setSelectedType] = useState<string>(ALL_TYPES);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const {
    data: typesData,
    isLoading: isLoadingTypes,
    error: typesError,
  } = useNotificationTypes();

  const { data, isLoading, isFetching, error } = useNotificationKeysValues({
    interfaceLanguage: language,
    notificationType: selectedType === ALL_TYPES ? undefined : selectedType,
  });

  const typeOptions = useMemo(
    () => [
      { value: ALL_TYPES, label: "All notification types" },
      ...(typesData?.notificationTypes ?? []).map((type) => ({
        value: type,
        label: type,
      })),
    ],
    [typesData?.notificationTypes]
  );

  const keysValues = useMemo(
    () => data?.keysValues ?? [],
    [data?.keysValues]
  );

  const groups = useMemo(
    () =>
      groupKeysValuesByType(keysValues).map((group) => ({
        ...group,
        missing: group.entries.reduce(
          (sum, entry) => sum + countMissingValues(entry),
          0
        ),
      })),
    [keysValues]
  );

  const totalMissing = groups.reduce((sum, group) => sum + group.missing, 0);
  const affectedTypes = groups.filter((group) => group.missing > 0).length;

  const visibleGroups = useMemo(
    () => (onlyMissing ? groups.filter((group) => group.missing > 0) : groups),
    [groups, onlyMissing]
  );

  // Nothing to narrow down once the language is fully translated.
  useEffect(() => {
    if (totalMissing === 0) setOnlyMissing(false);
  }, [totalMissing]);

  useEffect(() => {
    if (!error) return;
    notification.error({
      key: "notification-keys-values-load-error",
      message: "Error",
      description: getApiErrorMessage(
        error,
        "Failed to load the localized preview."
      ),
      placement: "top",
    });
  }, [error]);

  useEffect(() => {
    if (!typesError) return;
    notification.error({
      key: "notification-types-load-error",
      message: "Error",
      description: getApiErrorMessage(
        typesError,
        "Failed to load notification types."
      ),
      placement: "top",
    });
  }, [typesError]);

  return (
    <div className="flex flex-col gap-20 w-full min-w-0">
      <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-20 sm:p-30 w-full min-w-0">
        <div className="flex items-center gap-10 mb-20 flex-wrap">
          <h3 className="text-dark-grey font-bold text-lg m-0">
            Localized Preview
          </h3>
          {isFetching && <Spinner />}
          <span className="text-sm text-dark-grey" style={{ opacity: 0.55 }}>
            Placeholders like <code>{"{{firstName}}"}</code> are shown exactly as
            stored; an empty value means the key is missing from that language.
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-20 mb-20">
          <div className="flex flex-col gap-10 min-w-0">
            <FieldLabel>Interface language</FieldLabel>
            <DropdownWithSearch
              options={LANGUAGE_OPTIONS}
              placeholder="Select language..."
              value={language}
              onChange={(option) =>
                setLanguage(option?.value ?? DEFAULT_PREVIEW_LANGUAGE)
              }
              className="w-full"
              id="notification-preview-language"
            />
          </div>

          <div className="flex flex-col gap-10 min-w-0">
            <FieldLabel>Notification type</FieldLabel>
            {isLoadingTypes ? (
              <div className="flex items-center gap-2">
                <Spinner />
                <span
                  className="text-sm text-dark-grey"
                  style={{ opacity: 0.5 }}
                >
                  Loading types...
                </span>
              </div>
            ) : (
              <DropdownWithSearch
                options={typeOptions}
                placeholder="All notification types"
                value={selectedType}
                onChange={(option) => setSelectedType(option?.value ?? ALL_TYPES)}
                className="w-full"
                id="notification-preview-type"
              />
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-20 flex-wrap">
          <div className="flex items-center gap-10 flex-wrap">
            <CountBadge>
              {groups.length} type{groups.length === 1 ? "" : "s"}
            </CountBadge>
            <CountBadge>
              {keysValues.length} key set{keysValues.length === 1 ? "" : "s"}
            </CountBadge>
            <CountBadge tone={totalMissing > 0 ? "error" : "success"}>
              {totalMissing > 0
                ? `${totalMissing} missing in ${language}`
                : `All keys present in ${language}`}
            </CountBadge>
            {affectedTypes > 0 && (
              <CountBadge tone="error">
                {affectedTypes} type{affectedTypes === 1 ? "" : "s"} affected
              </CountBadge>
            )}
          </div>

          {totalMissing > 0 && (
            <div className="inline-flex flex-wrap rounded-md border border-light-grey-3 border-solid overflow-hidden">
              <button
                type="button"
                onClick={() => setOnlyMissing(false)}
                className={`px-10 py-10 text-sm font-semibold transition-colors cursor-pointer ${
                  onlyMissing
                    ? "bg-white text-dark-grey"
                    : "bg-primary text-white"
                }`}
              >
                All types
              </button>
              <button
                type="button"
                onClick={() => setOnlyMissing(true)}
                className={`px-10 py-10 text-sm font-semibold border-l border-light-grey-3 border-solid transition-colors cursor-pointer ${
                  onlyMissing
                    ? "bg-primary text-white"
                    : "bg-white text-dark-grey"
                }`}
              >
                Only missing ({affectedTypes})
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-30 flex items-center justify-center gap-10">
          <Spinner />
          <span className="text-sm text-dark-grey" style={{ opacity: 0.5 }}>
            Loading preview...
          </span>
        </div>
      ) : visibleGroups.length === 0 ? (
        <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-30">
          <p className="text-sm text-dark-grey m-0" style={{ opacity: 0.6 }}>
            {onlyMissing
              ? `Nothing missing in ${language} for this selection.`
              : "No key sets returned for this selection."}
          </p>
        </div>
      ) : (
        visibleGroups.map((group) => (
          <div
            key={group.notificationType}
            className="bg-white rounded-xl border border-light-grey-3 border-solid p-20 sm:p-30 w-full min-w-0"
            style={
              group.missing > 0
                ? { borderLeft: "3px solid #FDA29B" }
                : undefined
            }
          >
            <div
              className="flex items-center gap-10 mb-20 flex-wrap pb-10"
              style={{ borderBottom: "1px solid #EEF0F4" }}
            >
              <h3
                className="text-dark-grey font-bold m-0"
                style={{
                  fontFamily: MONO_FONT,
                  fontSize: 18,
                  lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                  overflowWrap: "anywhere",
                }}
              >
                {group.notificationType}
              </h3>
              <CountBadge tone={group.entries.length > 1 ? "accent" : "neutral"}>
                {group.entries.length} key set
                {group.entries.length === 1 ? "" : "s"}
              </CountBadge>
              {group.missing > 0 && (
                <CountBadge tone="error">
                  {group.missing} missing value
                  {group.missing === 1 ? "" : "s"}
                </CountBadge>
              )}
            </div>

            {group.entries.length > 1 ? (
              <Tabs
                defaultActiveKey={String(group.entries[0].variant)}
                items={group.entries.map((entry) => {
                  const missing = countMissingValues(entry);
                  return {
                    key: String(entry.variant),
                    label: (
                      <span className="flex items-center gap-10">
                        Variant {entry.variant}
                        {missing > 0 && (
                          <CountBadge tone="error">{missing}</CountBadge>
                        )}
                      </span>
                    ),
                    children: <KeySetBody entry={entry} language={language} />,
                  };
                })}
              />
            ) : (
              <KeySetBody entry={group.entries[0]} language={language} />
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default NotificationPreviewPanel;
